/**
 * @component group-add-thread-modal
 * @css group-add-thread-modal.css
 * @primitives .btn.btn-primary, .btn.btn-secondary
 */
import { escapeHtml } from '../utils/html-utils.js';
import { THREAD_ICON } from '../utils/group-threads.js';

class GroupAddThreadModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.groupId = null;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  open(groupId) {
    this.groupId = groupId;
    this.isOpen = true;
    this.render();
    document.body.style.overflow = 'hidden';
    const nameInput = this.querySelector('#threadNameInput');
    nameInput?.focus();
  }

  close() {
    this.isOpen = false;
    this.isSubmitting = false;
    this.render();
    document.body.style.overflow = '';
  }

  render() {
    this.innerHTML = `
      <div class="group-add-thread-modal ${this.isOpen ? 'open' : ''}" id="groupAddThreadModal">
        <div class="modal-backdrop" id="modalBackdrop"></div>
        <div class="modal-container">
          <div class="modal-header">
            <h3>${THREAD_ICON} Add Thread</h3>
            <button type="button" class="close-btn" id="closeBtn" aria-label="Close">×</button>
          </div>
          <div class="modal-body">
            <p class="modal-description">Threads are lightweight side conversations inside your group.</p>
            <form id="addThreadForm">
              <div class="form-group">
                <label for="threadNameInput">Thread name</label>
                <input
                  type="text"
                  id="threadNameInput"
                  name="thread_name"
                  maxlength="140"
                  required
                  placeholder="e.g. Seed swaps"
                />
              </div>
              <div class="form-group">
                <label for="threadDescInput">Description (optional)</label>
                <textarea
                  id="threadDescInput"
                  name="thread_description"
                  rows="3"
                  maxlength="500"
                  placeholder="What belongs in this thread?"
                ></textarea>
              </div>
              <div class="form-actions">
                <button type="button" class="btn btn-secondary" id="cancelBtn">Cancel</button>
                <button type="submit" class="btn btn-primary" id="submitBtn">
                  <span class="btn-text">Add Thread</span>
                  <span class="btn-loading" style="display: none;">Adding…</span>
                </button>
              </div>
            </form>
            <div class="alert-container"></div>
          </div>
        </div>
      </div>
    `;
    if (this.isOpen) {
      this.attachEventListeners();
    }
  }

  attachEventListeners() {
    this.querySelector('#modalBackdrop')?.addEventListener('click', () => this.close());
    this.querySelector('#closeBtn')?.addEventListener('click', () => this.close());
    this.querySelector('#cancelBtn')?.addEventListener('click', () => this.close());
    this.querySelector('#addThreadForm')?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  async handleSubmit() {
    if (this.isSubmitting || !this.groupId) return;
    const form = this.querySelector('#addThreadForm');
    const name = form.querySelector('[name="thread_name"]')?.value?.trim();
    const description = form.querySelector('[name="thread_description"]')?.value?.trim() || '';
    if (!name) return;

    this.isSubmitting = true;
    const submitBtn = this.querySelector('#submitBtn');
    const btnText = submitBtn?.querySelector('.btn-text');
    const btnLoading = submitBtn?.querySelector('.btn-loading');
    if (submitBtn) submitBtn.disabled = true;
    if (btnText) btnText.style.display = 'none';
    if (btnLoading) btnLoading.style.display = 'inline';

    try {
      await window.API.request(`/groups/${this.groupId}/threads`, {
        method: 'POST',
        body: JSON.stringify({ name, description })
      });
      this.dispatchEvent(new CustomEvent('group-threads-changed', { bubbles: true }));
      form.reset();
      this.close();
    } catch (error) {
      const container = this.querySelector('.alert-container');
      if (container) {
        container.innerHTML = `<div class="alert alert-error">${escapeHtml(error.message || 'Could not create thread')}</div>`;
      }
    } finally {
      this.isSubmitting = false;
      if (submitBtn) submitBtn.disabled = false;
      if (btnText) btnText.style.display = '';
      if (btnLoading) btnLoading.style.display = 'none';
    }
  }
}

customElements.define('group-add-thread-modal', GroupAddThreadModal);
export default GroupAddThreadModal;
